// client/src/pages/Landing.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CheckIcon } from '@heroicons/react/24/solid';

interface LoginFormData {
  email: string;
  password: string;
}

// Feature bullet point component
const FeatureBullet: React.FC<{text: string}> = ({ text }) => (
  <div className="flex items-center mt-2">
    <CheckIcon className="h-5 w-5 text-blue-400 mr-2 flex-shrink-0" />
    <span className="text-gray-200">{text}</span>
  </div>
);

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, isLoading: authLoading } = useAuth();
  const [credentials, setCredentials] = useState<LoginFormData>({
    email: '',
    password: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
  
    try {
      await login(credentials.email, credentials.password);
      // Navigation is now handled by the route component
    } catch (error: any) {
      setError(error.message || 'Login failed. Please try again.');
      // Clear password field on error
      setCredentials(prev => ({ ...prev, password: '' }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/utilities/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col justify-center" 
         style={{ 
           background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
           backgroundSize: 'cover',
           backgroundPosition: 'center',
           fontFamily: '"Inter", sans-serif'
         }}>
      {/* Main Content Container */}
      <div className="max-w-7xl mx-auto py-12 px-4 sm:py-16 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
          
          {/* Left Side - Hero Content */}
          <div className="w-full lg:w-1/2 text-center lg:text-left">
            {/* Logo */}
            <div className="flex justify-center lg:justify-start mb-8">
              <img src="/utilities_logo.png" alt="TPI Compliance Logo" className="h-96 w-auto" />
            </div>
            
            {/* USP Headline */}
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Power your brokerage <br className="hidden md:block" />
              <span className="text-blue-400">with clarity.</span>
            </h1>
            
            {/* Supporting Text */}
            <p className="text-xl text-gray-300 mb-6">
              All-in-one CRM for utility brokers, with contract alerts, LOAs, and built-in automation.
            </p>
            
            {/* Feature Bullets */}
            <div className="mt-8 space-y-2">
              <FeatureBullet text="Lead-to-contract workflow" />
              <FeatureBullet text="LOA generation + digital signing" />
              <FeatureBullet text="Contract renewal alerts" />
            </div>
          </div>
          
          {/* Right Side - Login Form */}
          <div className="w-full lg:w-5/12 mt-10 lg:mt-0">
            <div className="backdrop-blur-sm bg-white/10 rounded-xl shadow-2xl overflow-hidden border border-white/20"
                 style={{ backdropFilter: 'blur(12px)' }}>
              
              {/* Form Header */}
              <div className="px-6 py-4 border-b border-white/10">
                <h2 className="text-xl font-semibold text-white">Sign In</h2>
              </div>
              
              {/* Form Body */}
              <div className="p-6">
                <form className="space-y-5" onSubmit={handleLogin}>
                  {error && (
                    <div className="rounded-md bg-red-400/20 p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-red-300" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <div className="text-sm text-red-200">{error}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Email Field */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-200 mb-1">
                      Email
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                        </svg>
                      </div>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        value={credentials.email}
                        onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                        className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-600 rounded-lg bg-gray-700/50 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="you@example.com"
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  {/* Password Field */}
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-200 mb-1">
                      Password
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                      <input
                        id="password"
                        name="password"
                        type="password"
                        autoComplete="current-password"
                        required
                        value={credentials.password}
                        onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                        className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-600 rounded-lg bg-gray-700/50 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="••••••••"
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  {/* Forgot Password Link */}
                  <div className="flex items-center justify-end">
                    <Link to="/forgot-password" className="text-sm text-blue-400 hover:text-blue-300">
                      Forgotten Password?
                    </Link>
                  </div>

                  {/* Submit Button */}
                  <div>
                    <button
                      type="submit"
                      disabled={isLoading}
                      className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-base font-medium text-white ${
                        isLoading ? 'bg-blue-500/70 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                      }`}
                    >
                      {isLoading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Signing in...
                        </>
                      ) : (
                        'Sign in'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
            
            {/* CTA Link */}
            <div className="mt-6 text-center">
              <p className="text-gray-300 text-sm">
                New to TPI Compliance? <button type="button" className="text-blue-400 hover:text-blue-300 font-medium bg-transparent border-0 cursor-pointer underline">Request a Demo</button>
              </p>
            </div>
          </div>
        </div>
      </div>
      

    </div>
  );
};

export default Landing;